import React, { useMemo } from "react";

import {
  mdiCheckCircleOutline,
  mdiFileDocumentOutline,
  mdiFileMusicOutline,
  mdiFileVideoOutline,
  mdiInformation,
  mdiTrashCanOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import DetectedTracks from "./DetectedTracks";
import prettyDurations, { DURATION_UNITS } from "../../../utils/durations";
import { isPivotMedia } from "../../../utils/typePredicate";

import type {
  PivotMedia,
  SourceMedia,
  Stream,
} from "../../../api/EncoderAPI.types";

function getFileIcon(streamList: Stream[], mimeType?: string): string {
  const isVideo =
    streamList.some((stream) => stream.codec_type === "video") ||
    mimeType?.startsWith("video/");
  const isAudio =
    streamList.some((stream) => stream.codec_type === "audio") ||
    mimeType?.startsWith("audio/");
  if (isVideo) return mdiFileVideoOutline;
  if (isAudio) return mdiFileMusicOutline;
  return mdiFileDocumentOutline;
}

interface SourceFileCardProps {
  source: SourceMedia | PivotMedia;
  isEncoding: boolean;
  isSimpleMode?: boolean;
  isUploading?: boolean;
  isQueued?: boolean;
  isUploadError?: boolean;
  uploadProgress?: number;
  mimeType?: string;
  onDelete?: () => void;
}

export default function SourceFileCard({
  source,
  isEncoding,
  isSimpleMode,
  isUploading,
  isQueued,
  isUploadError,
  uploadProgress = 0,
  mimeType,
  onDelete,
}: SourceFileCardProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const streamList = source.info?.streams ?? [];
  const videoStream = streamList.find((s) => s.codec_type === "video");
  const format = source.info?.format;

  const fileIcon = getFileIcon(streamList, mimeType);

  const metaParts = useMemo(() => {
    const parts: string[] = [];
    if (videoStream?.duration) {
      const durationMs = parseFloat(videoStream.duration) * 1000;
      const { humanised } = prettyDurations(durationMs, {
        valueUnit: DURATION_UNITS.MILLISECONDS,
        format: { noDays: true },
      });
      if (humanised) parts.push(humanised);
    }
    if (videoStream?.height) parts.push(`${videoStream.height}p`);
    const avgFrameRate = Number(videoStream?.avg_frame_rate);
    if (avgFrameRate) {
      parts.push(`${Math.round(avgFrameRate)} fps`);
    }
    return parts;
  }, [videoStream?.duration, videoStream?.height, videoStream?.avg_frame_rate]);

  const isEncodingError = isPivotMedia(source)
    ? source.encoding_status === "error"
    : source.status === "error";

  const barProgress = isUploading ? uploadProgress : 100;
  // A queued upload has no meaningful progress yet — show "queued" instead of a
  // 0% bar.
  const showBar = isUploading && !isQueued;
  const isMediaReady = isPivotMedia(source)
    ? source.encoding_status === "transcoded"
    : source.available;

  const showTracks =
    isMediaReady &&
    streamList.some(
      (s) => s.codec_type === "video" || s.codec_type === "audio",
    );

  const isWebVtt = format?.format_name === "webvtt";

  const showDeleteButton =
    (isWebVtt || isPivotMedia(source)) && (!isUploading || isUploadError);

  const cardClassNames = classnames(
    "overflow-hidden rounded-md border bg-muted",
    isUploadError || isEncodingError
      ? "border-destructive/30"
      : "border-border",
  );
  const progressBarClassNames = classnames(
    "h-full transition-all duration-500",
    barProgress === 100 ? "bg-status-active" : "bg-primary",
  );
  const uploadProgressClassNames = classnames(
    uploadProgress === 100 ? "text-status-active" : "text-primary",
  );
  const deleteButtonClassNames = classnames(
    "shrink-0 rounded-md p-1 text-subtle disabled:cursor-not-allowed",
    !isEncoding && "hover:bg-destructive/10 hover:text-destructive",
  );

  function getStatusLabel(): React.JSX.Element {
    if (isMediaReady) {
      return (
        <span className="text-status-ready inline-flex items-center gap-1">
          {t("ready")}
          <Icon path={mdiCheckCircleOutline} size={0.6} />
        </span>
      );
    }
    if (isQueued) {
      return (
        <span className="text-muted-foreground">{t("queuedForUpload")}</span>
      );
    }
    if (isUploading) {
      return (
        <span className={uploadProgressClassNames}>{uploadProgress}%</span>
      );
    }
    if (isUploadError) {
      return <span className="text-destructive">{t("uploadFailed")}</span>;
    }
    if (isEncodingError) {
      return <span className="text-destructive">{t("error")}</span>;
    }
    return <span className="text-muted-foreground">{t("processing")}</span>;
  }

  return (
    <div className={cardClassNames}>
      <div className="flex items-center gap-[0.78125rem] px-4 py-3">
        <div className="border-border bg-card flex size-10 shrink-0 items-center justify-center rounded-md border">
          <Icon className="text-muted-foreground" path={fileIcon} size={1} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[0.78125rem] font-medium">
            {source.filename}
          </p>
          <div className="mt-1 flex items-center gap-1">
            {format?.format_long_name ? (
              <span className="text-muted-foreground text-xs">
                {format.format_long_name}
              </span>
            ) : null}
            {isWebVtt ? (
              <span
                className="text-muted-foreground"
                data-tooltip-content={t("updateVttInDrawer")}
                data-tooltip-id={APP_TOOLTIP_ID}
              >
                <Icon path={mdiInformation} size={0.6} />
              </span>
            ) : null}
          </div>
        </div>
        <div className="text-right">
          {metaParts.length > 0 && (
            <p className="text-muted-foreground text-[0.78125rem]">
              {metaParts.join(" - ")}
            </p>
          )}
          <p className="text-[0.78125rem] font-medium">{getStatusLabel()}</p>
        </div>
        {showDeleteButton ? (
          <button
            aria-label="Remove source media"
            className={deleteButtonClassNames}
            data-tooltip-id={APP_TOOLTIP_ID}
            disabled={isEncoding}
            onClick={onDelete}
            type="button"
            data-tooltip-content={
              isEncoding ? t("cannotDeleteWhileEncoding") : null
            }
          >
            <Icon path={mdiTrashCanOutline} size={0.8} />
          </button>
        ) : null}
      </div>
      {showBar ? (
        <div className="bg-surface-muted mx-4 mb-3 h-1.5 overflow-hidden rounded-full">
          <div
            className={progressBarClassNames}
            style={{ width: `${barProgress}%` }}
          />
        </div>
      ) : null}
      {showTracks && !isSimpleMode ? (
        <DetectedTracks streamList={streamList} />
      ) : null}
    </div>
  );
}
